import { useEffect, useRef, useState } from "react";

import { faCalendarDays, faEdit, faInfoCircle, faRepeat } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormControlLabel, Checkbox as MUICheckbox, Stack, TextField } from "@mui/material";
import type { FieldErrors } from "react-hook-form";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetTreatmentSchedules } from "api/services/treatment/hooks";
import type {
  DurationWeeks,
  HealthFormType,
  PostTreatmentSchedulePayload,
} from "api/services/treatment/schemas/Treatment";
import { JointImage } from "routes/patients/PatientProfile/components/UserCourse/components/Templates";
import { BaseButton } from "shared/atoms/BaseButton";
import { MUISelect } from "shared/atoms/MUI";
import { sortAlphabetically } from "utils/array/sort";
import { useGroupOptions, useRepsOptions, useSetsOptions } from "utils/form";
import { translateTemplateGroup, translateTemplateKey } from "utils/translations";

import { SettingsTemplateSkeleton } from "../Skeleton";
import type { HandleApplySetsAndRepsToAll, TreatmentEditorSettings } from "../types";
import { DEFAULT_REPS, DEFAULT_SETS } from "../utils";

import { Section } from "./Section";
import {
  CheckboxWrapper,
  CompactTemplateItem,
  Container,
  Content,
  CustomWeeksInput,
  DurationWeeksToggle,
  EmptyState,
  FilterSection,
  HealthFormWarning,
  LengthGrid,
  LengthOption,
  TemplateIconWrapper,
  TemplateInfo,
  TemplateName,
  TemplateType,
  TemplatesContent,
  TemplatesList,
  ToggleOption,
  ToggleSlider,
  ToggleTrack,
} from "./styles";

export const DURATION_WEEKS_OPTIONS = [6, 8, 12, 16, 20];
const EXERCISES_PER_DAY_LIMIT = 12;

interface SettingsSidebarProps {
  durationWeeks: DurationWeeks;
  editorSettings: TreatmentEditorSettings;
  formErrors: FieldErrors<PostTreatmentSchedulePayload>;
  healthFormMismatch?: boolean;
  patientAilment?: string;
  patientPrimaryJoint?: string;
  onApplySetsAndRepsToAll: HandleApplySetsAndRepsToAll;
  onDurationWeeksChange: (length: DurationWeeks) => void;
  onLoadTemplate: (templateId: number) => void;
}

export const SettingsSidebar: React.FC<SettingsSidebarProps> = ({
  durationWeeks,
  editorSettings: { context, mode },
  formErrors,
  healthFormMismatch = false,
  patientAilment,
  patientPrimaryJoint,
  onApplySetsAndRepsToAll,
  onDurationWeeksChange,
  onLoadTemplate,
}) => {
  const { t } = useTranslation();
  const { control } = useFormContext<PostTreatmentSchedulePayload>();
  const setsOptions = useSetsOptions();
  const repsOptions = useRepsOptions();
  const [templateBodyAreaFilter, setTemplateBodyAreaFilter] = useState<string>("all");
  const [visibleTemplateCount, setVisibleTemplateCount] = useState(10);
  const [isEditingWeeks, setIsEditingWeeks] = useState(false);
  const [customWeeksValue, setCustomWeeksValue] = useState("");
  const [customWeeksError, setCustomWeeksError] = useState(false);
  const [durationWeeksMode, setDurationWeeksMode] = useState<"ongoing" | "set_weeks">("ongoing");
  const [sets, setSets] = useState<number>(DEFAULT_SETS);
  const [reps, setReps] = useState<string>(DEFAULT_REPS);
  const customWeeksInputRef = useRef<HTMLInputElement>(null);
  const templatesListRef = useRef<HTMLDivElement>(null);
  const isFieldDisabled = mode === "edit";

  useEffect(() => {
    setDurationWeeksMode(durationWeeks === null ? "ongoing" : "set_weeks");
  }, [durationWeeks]);

  const {
    data,
    isLoading: templatesLoading,
    isFetched: templatesFetched,
  } = useGetTreatmentSchedules(
    {
      limit: 1000,
    },
    { enabled: !isFieldDisabled }
  );
  const templates = data?.items || [];

  const groupOptions = useGroupOptions();

  const exercisesPerDayOptions = Array.from({ length: EXERCISES_PER_DAY_LIMIT }, (_, i) => {
    const count = i + 1;
    return {
      label: t("common.exercises_icu", { count }),
      value: count,
    };
  });

  const handleDurationWeeksModeChange = (weekMode: "ongoing" | "set_weeks") => {
    setDurationWeeksMode(weekMode);
    if (weekMode === "ongoing") {
      onDurationWeeksChange(null);
    } else {
      onDurationWeeksChange(12);
    }
  };

  const handleCustomWeeksSubmit = () => {
    const value = parseInt(customWeeksValue, 10);
    if (!Number.isNaN(value) && value > 0 && value <= 52) {
      onDurationWeeksChange(value);
      setIsEditingWeeks(false);
      setCustomWeeksValue("");
      setCustomWeeksError(false);
    } else {
      setCustomWeeksError(true);
    }
  };

  const handleCustomWeeksKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleCustomWeeksSubmit();
    } else if (e.key === "Escape") {
      setIsEditingWeeks(false);
      setCustomWeeksValue("");
      setCustomWeeksError(false);
    }
  };

  const handleCustomWeeksChange = (value: string) => {
    setCustomWeeksValue(value);
    setCustomWeeksError(false);
  };

  useEffect(() => {
    if (isEditingWeeks && customWeeksInputRef.current) {
      customWeeksInputRef.current.focus();
    }
  }, [isEditingWeeks]);

  const healthFormOptions: { label: string; value: HealthFormType }[] = [
    { label: t("usercourse.healthformtype.fall_prevention"), value: "fall_prevention" },
    { label: t("usercourse.healthformtype.generic"), value: "generic" },
    { label: t("usercourse.healthformtype.hand_oa"), value: "hand_oa" },
    { label: t("usercourse.healthformtype.hip_oa"), value: "hip_oa" },
    { label: t("usercourse.healthformtype.knee_oa"), value: "knee_oa" },
    { label: t("usercourse.healthformtype.low_back_pain"), value: "low_back_pain" },
    { label: t("usercourse.healthformtype.neck"), value: "neck" },
    { label: t("usercourse.healthformtype.osteoporosis"), value: "osteoporosis" },
    { label: t("usercourse.healthformtype.shoulder_pain"), value: "shoulder_pain" },
  ];

  const filteredTemplates =
    templates?.filter(template => {
      if (templateBodyAreaFilter !== "all" && template.group !== templateBodyAreaFilter) return false;
      return true;
    }) || [];

  const displayedTemplates = filteredTemplates.slice(0, visibleTemplateCount);

  const templateCountsByGroup =
    templates?.reduce((acc, template) => {
      if (template.group) {
        acc[template.group] = (acc[template.group] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>) || {};

  const bodyAreas = sortAlphabetically({
    list: [
      {
        label: t("treatment.template_selection.all_templates", { count: templates?.length || 0 }),
        value: "all",
      },
      ...Array.from(
        new Set(
          templates
            ?.map(template => template.group)
            .filter((group): group is NonNullable<typeof group> => Boolean(group))
        )
      ).map(group => ({
        label: `${translateTemplateGroup(group)} (${templateCountsByGroup[group] || 0})`,
        value: String(group),
      })),
    ],
  });

  useEffect(() => {
    if (!patientPrimaryJoint || !templatesFetched) return;

    if (bodyAreas.some(({ value }) => value === patientPrimaryJoint)) {
      setTemplateBodyAreaFilter(patientPrimaryJoint);
    }
  }, [patientPrimaryJoint, templatesFetched]);

  useEffect(() => {
    setVisibleTemplateCount(10);
  }, [templateBodyAreaFilter]);

  // Handle scroll to load more templates
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const handleScroll = () => {
      const container = templatesListRef.current;
      if (!container) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const scrollThreshold = 50; // Load more when within 50px of bottom

      if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
        setVisibleTemplateCount(prev => {
          const newCount = prev + 10;
          // Don't exceed total filtered templates
          return Math.min(newCount, filteredTemplates.length);
        });
      }
    };

    const container = templatesListRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [filteredTemplates.length]);

  return (
    <Container>
      <Content>
        {context === "library" && (
          <>
            <Section title={t("treatment.editor.settings_sidebar.template_name")}>
              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || ""}
                    error={Boolean(formErrors.name)}
                    helperText={Boolean(formErrors.name) && t("errors.field.required")}
                  />
                )}
              />
            </Section>

            <Section title={t("treatment.editor.settings_sidebar.group")}>
              <Controller
                control={control}
                name="group"
                render={({ field: { ref, ...field } }) => (
                  <MUISelect
                    {...field}
                    options={groupOptions}
                    value={field.value || ""}
                    inputRef={ref}
                    error={Boolean(formErrors.group)}
                    formHelperText={formErrors.group ? t("errors.field.required") : undefined}
                  />
                )}
              />
            </Section>
          </>
        )}

        <Section title={t("treatment.editor.settings_sidebar.program_length")}>
          <DurationWeeksToggle>
            <ToggleTrack>
              <ToggleOption
                $active={durationWeeksMode === "ongoing"}
                onClick={() => handleDurationWeeksModeChange("ongoing")}
              >
                <FontAwesomeIcon icon={faRepeat} />
                {t("treatment.editor.settings_sidebar.ongoing")}
              </ToggleOption>
              <ToggleOption
                $active={durationWeeksMode === "set_weeks"}
                onClick={() => handleDurationWeeksModeChange("set_weeks")}
              >
                <FontAwesomeIcon icon={faCalendarDays} />
                {t("treatment.editor.settings_sidebar.set_weeks")}
              </ToggleOption>
              <ToggleSlider $position={durationWeeksMode === "ongoing" ? "left" : "right"} />
            </ToggleTrack>
          </DurationWeeksToggle>

          {durationWeeksMode === "set_weeks" && (
            <LengthGrid>
              {DURATION_WEEKS_OPTIONS.map(option => (
                <LengthOption
                  key={option}
                  $active={durationWeeks === option && !isEditingWeeks}
                  onClick={() => {
                    onDurationWeeksChange(option);
                    setIsEditingWeeks(false);
                  }}
                >
                  {option}w
                </LengthOption>
              ))}
              {isEditingWeeks ? (
                <CustomWeeksInput
                  ref={customWeeksInputRef}
                  type="number"
                  min="1"
                  max="52"
                  placeholder="Weeks"
                  value={customWeeksValue}
                  onChange={e => handleCustomWeeksChange(e.target.value)}
                  onKeyDown={handleCustomWeeksKeyDown}
                  onBlur={() => {
                    if (customWeeksValue) {
                      handleCustomWeeksSubmit();
                    } else {
                      setIsEditingWeeks(false);
                      setCustomWeeksError(false);
                    }
                  }}
                  $error={customWeeksError}
                />
              ) : (
                <LengthOption
                  $active={
                    durationWeeks ? !DURATION_WEEKS_OPTIONS.includes(durationWeeks) && durationWeeks !== null : false
                  }
                  onClick={() => {
                    setIsEditingWeeks(true);
                    setCustomWeeksValue(durationWeeks && durationWeeks > 0 ? durationWeeks.toString() : "12");
                  }}
                >
                  {durationWeeks && !DURATION_WEEKS_OPTIONS.includes(durationWeeks) && durationWeeks !== null ? (
                    `${durationWeeks}w`
                  ) : (
                    <FontAwesomeIcon icon={faEdit} />
                  )}
                </LengthOption>
              )}
            </LengthGrid>
          )}
        </Section>

        <Section title={t("treatment.editor.settings_sidebar.exercises_per_day")}>
          <Controller
            control={control}
            name="exercises_per_day"
            render={({ field: { ref, ...field } }) => (
              <MUISelect {...field} options={exercisesPerDayOptions} value={field.value} inputRef={ref} />
            )}
          />
        </Section>

        <Section title={t("treatment.editor.settings_sidebar.sets_and_reps")}>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
            <MUISelect
              label={t("library.exercises.fields.sets")}
              options={setsOptions}
              value={sets}
              onChange={event => setSets(event.target.value as number)}
              size="small"
            />
            <MUISelect
              label={t("library.exercises.fields.reps")}
              options={repsOptions}
              value={reps}
              onChange={event => setReps(event.target.value as string)}
              size="small"
            />
            <BaseButton
              variant="action"
              text={t("treatment.editor.settings_sidebar.sets_and_reps_apply")}
              icon="arrows-rotate"
              onClick={() => onApplySetsAndRepsToAll({ recommended_reps: reps, recommended_sets: sets })}
              style={{ marginLeft: "auto" }}
            />
          </Stack>
        </Section>

        <Section>
          <CheckboxWrapper>
            <Controller
              control={control}
              name="includes_functional_test"
              render={({ field }) => (
                <FormControlLabel
                  control={<MUICheckbox {...field} checked={field.value} disabled={isFieldDisabled} />}
                  label={t("treatment.editor.settings_sidebar.include_function_test")}
                />
              )}
            />
          </CheckboxWrapper>
        </Section>

        <Section title={t("treatment.editor.settings_sidebar.questionnaire")}>
          <Controller
            control={control}
            name="health_form_type"
            render={({ field: { ref, ...field } }) => (
              <MUISelect {...field} options={healthFormOptions} value={field.value} inputRef={ref} />
            )}
          />

          {healthFormMismatch && (
            <HealthFormWarning>
              <FontAwesomeIcon icon={faInfoCircle} />
              <span>
                {patientPrimaryJoint
                  ? t("treatment.editor.settings_sidebar.health_form_warning", { patientPrimaryJoint })
                  : t("treatment.editor.settings_sidebar.health_form_warning_ailment", { patientAilment })}
              </span>
            </HealthFormWarning>
          )}
        </Section>

        {mode === "build" && (
          <Section title={t("treatment.editor.settings_sidebar.templates")}>
            <TemplatesContent>
              {templatesLoading && <SettingsTemplateSkeleton />}

              {!templatesLoading && templates && templates.length > 0 && (
                <>
                  <FilterSection>
                    <MUISelect
                      options={bodyAreas}
                      value={templateBodyAreaFilter}
                      onChange={e => setTemplateBodyAreaFilter(e.target.value as HealthFormType)}
                      disabled={isFieldDisabled}
                      fullWidth
                    />
                  </FilterSection>

                  <TemplatesList ref={templatesListRef}>
                    {displayedTemplates.map(template => (
                      <CompactTemplateItem key={template.id} onClick={() => onLoadTemplate(template.id)}>
                        {template.key && (
                          <TemplateIconWrapper>
                            <JointImage id={template.key} />
                          </TemplateIconWrapper>
                        )}
                        <TemplateInfo>
                          <TemplateName>
                            {template.key
                              ? translateTemplateKey(template.key)
                              : template.name || `Template ${template.id}`}
                          </TemplateName>
                          <TemplateType>
                            {template.creator_id
                              ? t("treatment.editor.settings_sidebar.my_template")
                              : t("treatment.editor.settings_sidebar.ja_template")}
                          </TemplateType>
                        </TemplateInfo>
                      </CompactTemplateItem>
                    ))}
                  </TemplatesList>
                </>
              )}

              {!templatesLoading && (!templates || templates.length === 0) && (
                <EmptyState>{t("treatment.editor.settings_sidebar.no_templates")}</EmptyState>
              )}
            </TemplatesContent>
          </Section>
        )}
      </Content>
    </Container>
  );
};
